<template>
  <footer
    ref="$el"
    class="the-footer-brand-only"
    role="contentinfo"
    aria-label="Site footer">
    <!-- Desktop Layout -->
    <div
      class="the-footer-brand-only__desktop"
      role="navigation"
      aria-label="Footer navigation">
      <div class="the-footer-brand-only__desktop-grid">
        <aside
          class="the-footer-brand-only__social-column"
          role="complementary"
          aria-label="Social media and copyright">
          <div
            class="the-footer-brand-only__desktop-copyright"
            role="group"
            aria-label="Copyright information">
            <span class="the-footer-brand-only__desktop-copyright-top">{{ $t('footer.copyright', { year: new Date().getFullYear() }) }}</span>
            <span class="the-footer-brand-only__desktop-copyright-bottom">
              <base-link
                href="https://www.we-go.it/it/"
                target="_blank"
                aria-label="we-go"
                class="the-footer-brand-only__credits"
                rel="noopener noreferrer">
                <svg-bolded-by />
              </base-link>
            </span>
          </div>
        </aside>

        <!-- Branding Section -->
        <div
          class="the-footer-brand-only__branding"
          role="group"
          aria-label="Company information">
          <div class="the-footer-brand-only__logo">
            <nuxt-link
              :to="localePath('index')"
              class="the-footer-brand-only__logo-link"
              aria-label="Return to homepage">
              <logo-icon class="the-footer-brand-only__logo-svg" />
            </nuxt-link>
          </div>
          <nav
            class="the-footer-brand-only__legal-links"
            role="navigation"
            aria-label="Legal and policy links">
            <template
              v-for="link in legalLinks"
              :key="link.id">
              <a
                v-if="link.href.startsWith('http') || link.href.startsWith('/pdfs/')"
                :href="link.href"
                class="the-footer-brand-only__link"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="link.ariaLabel || link.label">
                {{ link.label }}
              </a>
              <nuxt-link
                v-else
                :to="link.href"
                class="the-footer-brand-only__link"
                :aria-label="link.ariaLabel || link.label">
                {{ link.label }}
              </nuxt-link>
            </template>
          </nav>
        </div>
      </div>
    </div>

    <!-- Mobile Layout -->
    <nav
      class="the-footer-brand-only__mobile"
      role="navigation"
      aria-label="Mobile footer navigation">
      <!-- Mobile Branding -->
      <div
        class="the-footer-brand-only__mobile-branding"
        role="group"
        aria-label="Company branding">
        <!-- Mobile Logo -->
        <div class="the-footer-brand-only__mobile-logo">
          <nuxt-link
            :to="localePath('index')"
            class="the-footer-brand-only__mobile-logo-link"
            aria-label="Return to homepage">
            <logo-icon class="the-footer-brand-only__mobile-logo-svg" />
          </nuxt-link>
        </div>

        <!-- Mobile Copyright -->
        <div
          class="the-footer-brand-only__mobile-copyright"
          role="group"
          aria-label="Copyright information">
          <span class="the-footer-brand-only__mobile-copyright-top">{{ $t('footer.copyright', { year: new Date().getFullYear() }) }}</span>
          <span class="the-footer-brand-only__mobile-copyright-bottom">
            <base-link
              href="https://www.we-go.it/it/"
              target="_blank"
              aria-label="we-go"
              class="the-footer-brand-only__credits"
              rel="noopener noreferrer">
              <svg-bolded-by />
            </base-link>
          </span>
        </div>
      </div>
    </nav>
  </footer>
</template>

<script setup>
  import { useIntersectionObserver } from '@vueuse/core'
  import { useI18n } from 'vue-i18n'

  import SvgBoldedBy from '~/assets/svgs/bolded-by.svg?component'
  import LogoIcon from '~/assets/svgs/logo.svg'

  const {
    t, locale
  } = useI18n()
  const localePath = useLocalePath()

  const legalLinks = computed(() => [
    {
      id: 'privacy', label: t('footer.privacyPolicy'), href: localePath('informativa-privacy')
    },
    {
      id: 'company', label: t('footer.companyInfo'), href: localePath('company-info')
    },
    {
      id: 'code-of-conduct',
      label: t('footer.codeOfConduct'),
      href: locale.value === 'it'
        ? '/pdfs/veneta_cucine_codice_di_comportamento_di_gruppo_2025.pdf'
        : '/pdfs/veneta_cucine_allegato_5_codice_di_comportamento_2022_en.pdf'
    },
    {
      id: 'whistleblowing', label: t('footer.whistleblowing'), href: '/pdfs/veneta_cucine_whistleblowing_2025.pdf'
    },
    {
      id: 'cookies', label: t('footer.cookiesInfo'), href: localePath('cookie-policy')
    },
    {
      id: 'netiquette', label: 'Netiquette Social Network', href: localePath('netiquette')
    }
  ])

  const { bus } = useNuxtApp()
  const $el = useTemplateRef('$el')

  // Recalculate scroll when Veneta Cucine footer is on screen
  const handleIntersect = ([ { isIntersecting } ]) => {
    if (isIntersecting) {
      bus?.emit?.('scroll:refresh')
    }
  }

  useIntersectionObserver($el, handleIntersect, {
    rootMargin: '0px',
    threshold: 0.25
  })
</script>

<style lang="scss" scoped>
  .the-footer-brand-only {
    background-color: var(--color-light-grey);
    padding: 58px 0 0;

    /* Desktop Layout */
    &__desktop {
      display: none;

      @include mq(desktop) {
        display: block;
        max-width: var(--size-width-margin);
        padding: 0 var(--space-m) var(--space-s);
      }
    }

    &__desktop-grid {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      grid-template-rows: auto;
      gap: var(--space-xl) 0;

      /* We handle spacing via padding/borders to ensure equal columns */
    }

    &__social-column {
      grid-column: 6;
      grid-row: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      border-left: 1px solid var(--color-grey);
      padding-left: var(--space-s);
      height: 100%;

      /* Ensure it takes full height */
    }

    &__social-nav {
      /* Wrapper for social links part */
    }

    /* Branding Section - Spans 4 columns on the second row */
    &__branding {
      grid-column: 1 / span 4;
      grid-row: 1;
      display: flex;
      flex-direction: column;
      gap: var(--space-m);
      margin-top: var(--space-xl);

      /* Align with the grid flow */
      padding-right: var(--space-s);
    }

    &__logo {
      display: flex;
      align-items: center;
      justify-content: flex-start;

      &-link {
        display: block;
        text-decoration: none;
        width: 100%;
        transition: opacity var(--timing-fast) var(--easing-default);

        &:hover {
          opacity: 0.8;
        }

        &:focus-visible {
          outline: 2px solid var(--color-red);
          outline-offset: 2px;
          border-radius: var(--space-xxxxs);
        }
      }

      a.router-link-exact-active {
        opacity: 1;
      }

      &-svg {
        width: 100%;
        height: auto;
        color: var(--color-dark-grey);
      }
    }

    &__legal-links {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-s);
    }

    &__link {
      @include body-xs;

      color: var(--color-dark-grey);
      text-decoration: none;
      padding: var(--space-xxxxs) 0;
      transition: color var(--timing-fast) var(--easing-default);
      position: relative;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 1px;
        background-color: currentcolor;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
      }

      &:hover {
        color: var(--color-dark-grey);

        &::after {
          opacity: 1;
        }
      }

      &:focus-visible {
        outline: 2px solid var(--color-red);
        outline-offset: 2px;
        border-radius: var(--space-xxxxs);
      }
    }

    &__desktop-copyright {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      opacity: 0.6;
      margin-top: auto;

      /* Push to bottom if needed */

      &-top,
      &-bottom {
        color: var(--color-black);

        @include body-xs;

        line-height: 1.4;
      }

      &-bottom {
        font-family: var(--font-wego, var(--font-family-poppins));
      }
    }

    &__credits {
      > svg {
        height: 12px;
        fill: currentcolor;
        transition: fill var(--timing-default);
      }

      &:hover {
        > svg {
          fill: #f1b7ff;
        }
      }
    }

    /* &__wego-link {
      color:
    /* Mobile Layout */
    &__mobile {
      display: block;
      padding: 0 var(--space-s) var(--space-n);

      @include mq(desktop) {
        display: none;
      }
    }

    &__mobile-branding {
      text-align: center;
    }

    &__mobile-logo {
      margin-bottom: var(--space-s);

      &-link {
        display: inline-block;
        text-decoration: none;
        transition: opacity var(--timing-fast) var(--easing-default);

        &:hover {
          opacity: 0.8;
        }

        &:focus-visible {
          outline: 2px solid var(--color-red);
          outline-offset: 2px;
          border-radius: var(--space-xxxxs);
        }
      }

      &-svg {
        width: 60vw;
        max-width: 900px;
        height: auto;
        color: var(--color-dark-grey);
      }
    }

    &__mobile-copyright {
      display: flex;
      flex-direction: column;
      gap: var(--space-xxs);
      opacity: 0.6;

      &-top,
      &-bottom {
        color: var(--color-black);

        @include body-xs;

        line-height: 1.4;
      }

      &-bottom {
        font-family: var(--font-wego, var(--font-family-poppins));
      }
    }
  }
</style>
